import React, { ChangeEvent, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import * as turf from '@turf/turf';
import _ from 'lodash';

import {
    Theme,
    makeStyles,
    Typography,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    IconButton,
    Menu,
    MenuItem,
    Checkbox
} from '@material-ui/core';
import MoreVertIcon from '@material-ui/icons/MoreVert';

import { MAIN_MAP } from '../../../../redux/map/Constants';
import {
    fetchAllDrawingsAsync,
    setFitBounds,
    addDrawFeature,
    setDrawnFeatures
} from '../../../../redux/map/Actions';
import { showSecondaryPanel } from '../../../../redux/panel/Actions';
import { NEW_SKETCHES } from '../../../../redux/panel/PanelNames';

import CreateSiteCardDialog from '../../../Common/SiteCard/CreateDialog';
import SimpleHeading from '../common/SimpleHeading';
import ScrollableContainer from '../../../utility/ScrollableContainer';
import DeleteSketchDialog from './DeleteSketchDialog';
import Circle from '../../../panels/LegendShapes/Circle';
import Polygon from '../../../panels/LegendShapes/Polygon';
import Line from '../../../panels/LegendShapes/Line';
import Point from '../../../panels/LegendShapes/Point';
import { StoreState } from 'app/javascript/bundles/App/redux/Store';

interface RowStructure {
    isChecked?: boolean;
    geometry: {
        type: string;
        coordinates: [];
    };
    properties: {
        id: number;
        description: null | string;
        fillColour: string;
        strokeColour: string;
        strokeStyle: 'dashed' | 'solid';
        radius?: number;
        centre?: [number, number];
        name: string;
    };
}

interface Styles {
    root: React.CSSProperties;
    typeFont: React.CSSProperties;
    border: React.CSSProperties;
    color: React.CSSProperties;
    padding: React.CSSProperties;
    tableShapeName: React.CSSProperties;
    shapeNameCell: React.CSSProperties;
}

const propTypes = {};

const defaultProps = {};

const useStyles = makeStyles<Theme, object, keyof Styles>(theme => styles(theme) as Styles);

function DrawnSketchesBody(): React.ReactElement {
    const classes = useStyles();
    const dispatch = useDispatch();

    const [menuOptionsEl, setMenuOptionsEl] = useState(null);
    const [anchorEl, setAnchorEl] = useState<(EventTarget & HTMLButtonElement) | null>(null);
    const [isCreateSiteCardDialogOpen, setIsCreateSiteCardDialogOpen] = useState(false);
    const [isDeleteSketchDialogOpen, setIsDeleteSketchDialogOpen] = useState(false);
    const [isParentChecked, setIsParentChecked] = useState(false);
    const [siteCardGeom, setSiteCardGeom] = useState({});
    const [name, setName] = useState('');
    const [id, setId] = useState(0);

    const drawModule = useSelector((state: any) => state[MAIN_MAP].drawModule);
    const projectId: number = useSelector((state: any) => state.project.currentProject?.id);
    const editDrawnFeatures = useSelector(
        (state: any) => state.user.features.drawnFeatures.editDrawnFeatures
    );
    const drawnFeatures = useSelector(
        (state: StoreState) => state[MAIN_MAP].drawnFeatures.features
    );

    const result = useSelector((state: any) => state[MAIN_MAP].savedDrawnFeatures);
    const [resultState, setResultState] = useState<RowStructure[]>([]);

    useEffect(() => {
        dispatchFetchDrawnFeaturesAsync(projectId);
    }, [projectId]);

    useEffect(() => {
        // This check is needed to prevent the isChecked value to be automatically changed to false
        if (resultState.length === 0 || resultState.length > result.length) {
            // Initialize state with isChecked property
            const initialState = result.map((item: any) => ({ ...item, isChecked: false }));
            setResultState(initialState);
        }
    }, [result]);

    function dispatchFetchDrawnFeaturesAsync(projectId: number): any {
        return dispatch(fetchAllDrawingsAsync(projectId));
    }
    function dispatchSetFitBounds(bounds: any): any {
        return dispatch(setFitBounds(bounds, { maxZoom: 20 }, MAIN_MAP));
    }
    function dispatchAddDrawnFeature(value: any): any {
        return dispatch(addDrawFeature(_.cloneDeep(value), MAIN_MAP));
    }
    function dispatchShowSecondaryPanel(name: string): any {
        return dispatch(showSecondaryPanel(name));
    }
    function dispatchSetDrawnFeatures(val: any): any {
        return dispatch(setDrawnFeatures(val, MAIN_MAP));
    }

    function generateMiniShape(item: any): any {
        if (item?.properties?.radius) {
            return (
                <Circle
                    key={item.id}
                    fillColour={item.properties?.fillColour}
                    strokeColour={item.properties?.strokeColour}
                    strokeStyle={item.properties?.strokeStyle}
                    needed={false}
                />
            );
        } else if (item?.geometry?.type === 'Polygon') {
            return (
                <Polygon
                    key={item.id}
                    fillColour={item.properties?.fillColour}
                    strokeColour={item.properties?.strokeColour}
                    strokeStyle={item.properties?.strokeStyle}
                    needed={false}
                />
            );
        } else if (item?.geometry?.type === 'LineString') {
            return (
                <Line
                    key={item.id}
                    strokeColour={item.properties?.strokeColour}
                    strokeStyle={item.properties?.strokeStyle}
                    needed={false}
                />
            );
        } else if (item?.geometry?.type === 'Point') {
            return (
                <Point key={item.id} strokeColour={item.properties?.strokeColour} needed={false} />
            );
        }
    }

    function displayShapeName(feature: {
        properties: { radius?: number };
        geometry: { type: string };
    }): any {
        if (feature?.properties?.radius) {
            return 'Circle';
        } else if (feature?.geometry?.type === 'Polygon') {
            return 'Polygon';
        } else if (feature?.geometry?.type === 'LineString') {
            return 'Line';
        } else if (feature?.geometry?.type === 'Point') {
            return 'Point';
        }
    }

    function zoomTo(row: any): any {
        const { geometry } = turf.envelope(row);
        dispatchSetFitBounds([geometry?.coordinates[0][0], geometry?.coordinates[0][2]]);

        // Check if shape is already displayed on the map
        if (drawnFeatures.some((obj: any) => obj?.properties?.id === row?.properties?.id)) {
            return;
        }

        dispatchAddDrawnFeature(row);
    }

    function editStyleForShape(row: any): any {
        dispatchShowSecondaryPanel(NEW_SKETCHES);
        const { geometry } = turf.envelope(row);

        dispatchSetFitBounds([geometry?.coordinates[0][0], geometry?.coordinates[0][2]]);
        // If it exists in drawnFeatures array, get the id and select the shape
        if (drawnFeatures.some((obj: any) => obj?.properties?.id === row?.properties?.id)) {
            const foundId = drawnFeatures.find(
                (obj: any) => obj?.properties?.id === row?.properties?.id
            );
            drawModule.getDrawControl().changeMode('direct_select', {
                featureId: foundId.id
            });
            return;
        }

        dispatchAddDrawnFeature(row);
    }

    function saveAsSiteCard(row: RowStructure): any {
        setIsCreateSiteCardDialogOpen(true);
        setSiteCardGeom(row.geometry);
        if (drawnFeatures.some((obj: any) => obj?.properties?.id === row?.properties?.id)) {
            return;
        }

        dispatchAddDrawnFeature(row);
    }

    function updateResultState(isChecked: boolean, id: number | string, row?: RowStructure) {
        setResultState(oldState =>
            oldState.map((res: any) => {
                if (id === 'p1') {
                    return { ...res, isChecked: isChecked };
                } else {
                    if (res.properties.id === id) {
                        displayIndividualSavedShapeOnMap(isChecked, row);
                        return { ...res, isChecked: isChecked };
                    }
                    return res;
                }
            })
        );
    }

    function changeCheckBoxStatus(
        isChecked: boolean,
        id: number | string,
        row?: RowStructure
    ): void {
        updateResultState(isChecked, id, row);
        const isAllChildsChecked = resultState.every((another: any) => another.isChecked === true);
        setIsParentChecked(isAllChildsChecked);

        if (id === 'p1') {
            displayAllSavedShapeOnMap(isChecked);
        }
    }

    function displayAllSavedShapeOnMap(isChecked: boolean): void {
        if (isChecked) {
            result.forEach((value: RowStructure) => {
                if (
                    !drawnFeatures.some((obj: any) => obj?.properties?.id === value?.properties?.id)
                ) {
                    dispatchAddDrawnFeature(value);
                }
            });
        } else {
            // If the parent checkbox is unchecked, it removes only the saved drawn features/shapes
            // and not the unsaved features/shapes drawn on the map
            drawnFeatures.map((item: any) => {
                if (
                    result.some(
                        (anotherItem: any) => anotherItem.properties.id === item.properties.id
                    )
                ) {
                    return drawModule.getDrawControl().delete(item.id);
                }
                return null;
            });
            dispatchSetDrawnFeatures(drawModule.getDrawControl().getAll());
        }
    }

    function displayIndividualSavedShapeOnMap(isChecked: boolean, row?: RowStructure): void {
        const foundFeature = drawModule
            .getDrawControl()
            .getAll()
            .features.find((obj: any) => obj?.properties?.id === row?.properties?.id);

        if (isChecked) {
            if (!foundFeature) {
                dispatchAddDrawnFeature(row);
            }
        } else if (foundFeature) {
            drawModule.getDrawControl().delete(foundFeature.id);
            dispatchSetDrawnFeatures(drawModule.getDrawControl().getAll());
        }
    }

    function deleteShape(row: any): any {
        setName(row.properties.name);
        setId(row.properties.id);
        setIsDeleteSketchDialogOpen(true);
    }

    function handleMenuClick(id: any, event: React.MouseEvent<HTMLButtonElement>): any {
        setAnchorEl(event.currentTarget);
        setMenuOptionsEl(id);
    }

    function handleMenuClose(): any {
        setAnchorEl(null);
        setMenuOptionsEl(null);
    }

    function generateDrawnListForPanel(): any {
        return (
            <TableContainer>
                <Table size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell classes={{ root: classes.tableShapeName }}>
                                <Checkbox
                                    checked={isParentChecked}
                                    onChange={(e): any =>
                                        changeCheckBoxStatus(e.target.checked, 'p1')
                                    }
                                />
                                Sketch Name
                            </TableCell>
                            <TableCell classes={{ root: classes.typeFont }} align="left">
                                Type
                            </TableCell>
                            <TableCell />
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {resultState.map((row: RowStructure) => (
                            <TableRow key={row?.properties.id}>
                                <TableCell
                                    component="th"
                                    scope="row"
                                    classes={{ root: classes.shapeNameCell }}>
                                    <div style={{ display: 'flex', alignItems: 'center' }}>
                                        <Checkbox
                                            checked={row?.isChecked}
                                            onChange={(e: ChangeEvent<HTMLInputElement>): any =>
                                                changeCheckBoxStatus(
                                                    e.target.checked,
                                                    row?.properties.id,
                                                    row
                                                )
                                            }
                                        />
                                        <div>{row?.properties.name}</div>
                                        <div style={{ marginLeft: '10px' }}>
                                            {generateMiniShape(row)}
                                        </div>
                                    </div>
                                </TableCell>
                                <TableCell classes={{ root: classes.shapeNameCell }} align="left">
                                    {displayShapeName(row)}
                                </TableCell>
                                <TableCell align="right" classes={{ root: classes.border }}>
                                    <IconButton
                                        size="small"
                                        onClick={(event): any =>
                                            handleMenuClick(row?.properties.id, event)
                                        }>
                                        <MoreVertIcon />
                                    </IconButton>
                                    <Menu
                                        open={menuOptionsEl === row?.properties.id}
                                        anchorEl={anchorEl}
                                        onClose={(): any => handleMenuClose()}>
                                        <MenuItem
                                            onClick={(): any => {
                                                zoomTo(row);
                                                handleMenuClose();
                                            }}>
                                            Zoom to
                                        </MenuItem>
                                        {editDrawnFeatures && (
                                            <MenuItem
                                                onClick={(): any => {
                                                    editStyleForShape(row);
                                                    handleMenuClose();
                                                }}>
                                                Edit style
                                            </MenuItem>
                                        )}
                                        {(row?.geometry?.type === 'Polygon' ||
                                            row?.geometry?.type === 'LineString') && (
                                            <MenuItem
                                                onClick={(): any => {
                                                    saveAsSiteCard(row);
                                                    handleMenuClose();
                                                }}>
                                                Save as site card
                                            </MenuItem>
                                        )}
                                        <MenuItem
                                            classes={{ root: classes.color }}
                                            onClick={(): any => {
                                                deleteShape(row);
                                                handleMenuClose();
                                            }}>
                                            Delete
                                        </MenuItem>
                                    </Menu>
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>
        );
    }

    useEffect(() => {
        resultState.forEach((row: RowStructure) => {
            const isPresent = drawnFeatures.some(
                (feature: any) => feature.properties.id === row.properties.id
            );
            const newRow = _.cloneDeep(row);
            newRow.isChecked = isPresent;
            changeCheckBoxStatus(isPresent, newRow?.properties.id, newRow);
        });
    }, [result, drawnFeatures]);

    return (
        <ScrollableContainer className={classes.root} grow={false}>
            <>
                <CreateSiteCardDialog
                    open={isCreateSiteCardDialogOpen}
                    onClose={(): any => setIsCreateSiteCardDialogOpen(false)}
                    defaultName="Drawn Shape"
                    featureConfig={{
                        isDrawn: true
                    }}
                    geometry={siteCardGeom}
                />
                <DeleteSketchDialog
                    open={isDeleteSketchDialogOpen}
                    onClose={(): any => setIsDeleteSketchDialogOpen(false)}
                    name={name}
                    id={id}
                />
                <SimpleHeading label="Saved sketches" />
                {result.length > 0 ? (
                    generateDrawnListForPanel()
                ) : (
                    <Typography variant="h3" color="textPrimary">
                        None found
                    </Typography>
                )}
            </>
        </ScrollableContainer>
    );
}

const styles = (theme: Theme): any => ({
    root: {
        padding: '20px 24px 24px 24px',
        '&> *:not(:last-child)': {
            marginBottom: theme.constants.verticalMargin
        }
    },
    tableShapeName: {
        fontSize: '16px',
        padding: 0
    },
    shapeNameCell: {
        fontSize: '16px',
        borderBottom: 'none',
        padding: '6px 24px 6px 0'
    },
    typeFont: {
        fontSize: '16px',
        padding: 0
    },
    border: {
        borderBottom: 'none'
    },
    color: {
        color: '#FF7981'
    },
    padding: {
        padding: 0
    }
});

DrawnSketchesBody.propTypes = propTypes;
DrawnSketchesBody.defaultProps = defaultProps;

export default DrawnSketchesBody;
